import { Dialog, DialogContent, DialogTitle, Input } from '@frontend-platform/ui-lib/ui';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUpdateMetric } from '../../../hooks/use-metrics';
import type { CalibrationMetric } from '../../../lib/types';
import {
  CRITERION_NAME_ERROR,
  CRITERION_NAME_PATTERN,
  CUSTOM_METRIC_FIELD_LIMITS,
} from '../constants';

interface RenameMetricModalProps {
  metric: CalibrationMetric;
  // Loaded metric library, for the uniqueness check (excludes the metric itself).
  existingMetrics: CalibrationMetric[];
  onRenamed: (event: { id: string; displayName: string }) => void;
  onClose: () => void;
}

const limits = CUSTOM_METRIC_FIELD_LIMITS;

export function RenameMetricModal({
  metric,
  existingMetrics,
  onRenamed,
  onClose,
}: RenameMetricModalProps) {
  const [name, setName] = useState(metric.displayName ?? '');
  const [touched, setTouched] = useState(false);
  const updateMetric = useUpdateMetric();

  const isNameTaken = useMemo(() => {
    const candidate = name.trim().toLowerCase();
    if (!candidate) return false;
    return existingMetrics.some(
      (m) => m.id !== metric.id && m.displayName?.trim().toLowerCase() === candidate
    );
  }, [name, existingMetrics, metric.id]);

  const error = useMemo(() => {
    if (!touched) return null;
    const trimmed = name.trim();
    if (!trimmed) return CRITERION_NAME_ERROR.REQUIRED;
    if (trimmed.length > limits.NAME_MAX) return CRITERION_NAME_ERROR.TOO_LONG;
    if (!CRITERION_NAME_PATTERN.test(trimmed)) return CRITERION_NAME_ERROR.INVALID_CHARS;
    if (isNameTaken) return CRITERION_NAME_ERROR.TAKEN;
    return null;
  }, [name, touched, isNameTaken]);

  const canSubmit =
    !!name.trim() &&
    name.trim().length <= limits.NAME_MAX &&
    CRITERION_NAME_PATTERN.test(name.trim()) &&
    !isNameTaken &&
    name.trim() !== metric.displayName;

  const onSave = async () => {
    if (!canSubmit) return;
    try {
      await updateMetric.mutateAsync({
        id: metric.id,
        payload: { displayName: name.trim() },
      });
      onRenamed({ id: metric.id, displayName: name.trim() });
    } catch {
      // Keep the dialog open so the user can retry; the error is already
      // surfaced by the query client's mutation handler.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-0 [&>button:last-child]:hidden">
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="font-semibold text-base text-foreground">
              Rename criterion
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="rename-metric-name" className="font-medium text-foreground text-sm">
              Name
            </label>
            <Input
              id="rename-metric-name"
              value={name}
              maxLength={limits.NAME_MAX}
              placeholder="Metric name"
              aria-invalid={!!error}
              onChange={(e) => {
                setName(e.target.value);
                setTouched(true);
              }}
            />
            <div className="flex items-center justify-between gap-2">
              {error ? <span className="text-red-600 text-xs">{error}</span> : <span />}
              <span className="text-muted-foreground text-xs">
                {name.length}/{limits.NAME_MAX}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border bg-background px-4 py-2 font-medium text-foreground text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canSubmit || updateMetric.isPending}
              onClick={onSave}
              className="rounded-lg bg-neutral-900 px-4 py-2 font-medium text-sm text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {updateMetric.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
